import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, ArrowRight, ArrowLeft, Users, User, Sparkles, Link2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const INTENTIONS = [
  "Financial alignment",
  "Health and lifestyle",
  "Relationship intentionality",
  "Long term life planning",
];

const STAGES = ["Dating", "Engaged", "Married"];
const CADENCES = ["Weekly", "Monthly", "Both"];

export default function OnboardingPage() {
  const { user, refreshProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [coupleName, setCoupleName] = useState("");
  const [stage, setStage] = useState("");
  const [intentions, setIntentions] = useState<string[]>([]);
  const [cadence, setCadence] = useState("Monthly");

  const [fullName, setFullName] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [personalIntention, setPersonalIntention] = useState("");

  const [partnerEmail, setPartnerEmail] = useState("");

  // Code-based joining
  const [joinCode, setJoinCode] = useState("");
  const [joiningByCode, setJoiningByCode] = useState(false);

  const toggleIntention = (i: string) => {
    setIntentions((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);
  };

  const handleJoinByCode = async () => {
    if (!joinCode.trim() || !fullName.trim()) {
      toast.error("Please enter your name and the linking code");
      return;
    }
    setJoiningByCode(true);
    try {
      const { data, error } = await supabase.functions.invoke("validate-workspace-code", {
        body: {
          code: joinCode.trim(),
          action: "join",
          full_name: fullName.trim(),
          preferred_name: preferredName.trim() || null,
          personal_intention: personalIntention.trim() || null,
        },
      });
      if (error) throw error;
      if (!data?.valid) { toast.error(data?.error || "Invalid or expired code"); setJoiningByCode(false); return; }
      await refreshProfile();
      toast.success("Welcome to Together OS 💛");
    } catch (err: any) {
      toast.error("Something went wrong", { description: err.message });
    } finally {
      setJoiningByCode(false);
    }
  };

  const handleComplete = async () => {
    if (!coupleName.trim() || !fullName.trim()) {
      toast.error("Please fill in required fields");
      return;
    }
    setLoading(true);
    try {
      const workspaceId = crypto.randomUUID();
      const { error: wsErr } = await supabase.from("couple_workspaces").insert({
        id: workspaceId,
        couple_name: coupleName.trim(),
        relationship_stage: stage || null,
        intentions: intentions.length > 0 ? intentions : null,
        reflection_cadence: cadence.toLowerCase(),
      });
      if (wsErr) throw wsErr;

      const { error: profErr } = await supabase.from("profiles").update({
        workspace_id: workspaceId,
        full_name: fullName.trim(),
        preferred_name: preferredName.trim() || null,
        personal_intention: personalIntention.trim() || null,
        onboarding_complete: true,
      }).eq("user_id", user!.id);
      if (profErr) throw profErr;

      if (partnerEmail.trim()) {
        await supabase.from("workspace_invites").insert({
          workspace_id: workspaceId,
          invited_by: user!.id,
          invited_email: partnerEmail.trim().toLowerCase(),
        });
        supabase.functions.invoke("send-invite-email", {
          body: {
            partnerEmail: partnerEmail.trim().toLowerCase(),
            inviterName: fullName.trim(),
            coupleName: coupleName.trim(),
            siteUrl: window.location.origin,
          },
        }).catch(console.error);
      }

      await refreshProfile();
      toast.success("Welcome to Together OS 💛");
    } catch (err: any) {
      toast.error("Something went wrong", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    // Step 0: Couple name
    <div key="couple" className="space-y-6">
      <div className="flex items-center gap-2 text-accent"><Users className="w-5 h-5" /><span className="text-sm font-medium">About us</span></div>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">What should we call you two?</label>
          <input value={coupleName} onChange={(e) => setCoupleName(e.target.value)} placeholder='e.g. "The Johnsons"' className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Relationship stage <span className="text-muted-foreground">(optional)</span></label>
          <div className="flex gap-2 flex-wrap">
            {STAGES.map((s) => (
              <button key={s} onClick={() => setStage(s === stage ? "" : s)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${stage === s ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>{s}</button>
            ))}
          </div>
        </div>
      </div>
    </div>,

    // Step 1: Intentions
    <div key="intentions" className="space-y-6">
      <div className="flex items-center gap-2 text-accent"><Sparkles className="w-5 h-5" /><span className="text-sm font-medium">Our intentions</span></div>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">What brings you here?</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {INTENTIONS.map((i) => (
              <button key={i} onClick={() => toggleIntention(i)} className={`px-4 py-3 rounded-lg text-sm font-medium text-left transition-colors ${intentions.includes(i) ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>{i}</button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">How often do you want to reflect?</label>
          <div className="flex gap-2">
            {CADENCES.map((c) => (
              <button key={c} onClick={() => setCadence(c)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${cadence === c ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>{c}</button>
            ))}
          </div>
        </div>
      </div>
    </div>,

    // Step 2: Individual details
    <div key="individual" className="space-y-6">
      <div className="flex items-center gap-2 text-accent"><User className="w-5 h-5" /><span className="text-sm font-medium">About you</span></div>
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Your name</label>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">What should we call you? <span className="text-muted-foreground">(optional)</span></label>
          <input value={preferredName} onChange={(e) => setPreferredName(e.target.value)} placeholder="Nickname" className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Your intention <span className="text-muted-foreground">(optional)</span></label>
          <textarea value={personalIntention} onChange={(e) => setPersonalIntention(e.target.value)} placeholder="What do you hope this brings?" className="w-full bg-background border border-input rounded-lg p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
      </div>
    </div>,

    // Step 3: Invite partner / code
    <div key="invite" className="space-y-6">
      <div className="flex items-center gap-2 text-accent"><Heart className="w-5 h-5" /><span className="text-sm font-medium">Connect with your partner</span></div>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground leading-relaxed">Together OS is built for two. Invite your partner or use a linking code.</p>

        {/* Email invite */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Invite by email</label>
          <input type="email" value={partnerEmail} onChange={(e) => setPartnerEmail(e.target.value)} placeholder="partner@email.com" className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>

        {/* Code join */}
        <div className="border-t border-border pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-muted-foreground" />
            <label className="text-sm font-medium text-foreground">Have a code from your partner?</label>
          </div>
          <div className="flex gap-2">
            <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="Enter 6-character code" className="flex-1 min-w-0 bg-background border border-input rounded-lg px-3 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-ring" maxLength={6} />
            <button onClick={handleJoinByCode} disabled={joiningByCode || !joinCode.trim() || !fullName.trim()} className="px-4 py-2.5 bg-accent text-accent-foreground rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 flex-shrink-0">
              {joiningByCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">This will skip workspace creation and join your partner's space.</p>
        </div>

        <p className="text-xs text-muted-foreground">You can skip both and invite later from settings.</p>
      </div>
    </div>,
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 overflow-x-hidden">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="w-5 h-5 text-accent" fill="currentColor" />
            <span className="font-display text-xl font-semibold text-foreground">Together OS</span>
          </div>
          <p className="text-muted-foreground">Let's set up your shared space.</p>
        </div>

        <div className="flex gap-1.5 mb-8">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-secondary"}`} />
          ))}
        </div>

        <div className="bg-card border border-border rounded-xl p-8">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              {steps[step]}
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between mt-8">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : <div />}

            {step < steps.length - 1 ? (
              <button onClick={() => setStep(step + 1)} disabled={step === 0 && !coupleName.trim()} className="flex items-center gap-1.5 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleComplete} disabled={loading || !fullName.trim()} className="flex items-center gap-1.5 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {loading ? "Setting up..." : "Begin Together"} <Heart className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
